import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import CustomStepBadge from '../../../components/OnboardingComponents/CustomStepBadge';
import CustomInput from '../../../components/OnboardingComponents/CustomInput';
import RoleSelect from '../../../components/OnboardingComponents/RoleSelect';
import CategoriesMultiselect from '../../../components/OnboardingComponents/CategoriesMultiselect';
import CustomDatePicker from '../../../components/OnboardingComponents/CustomDatePicker';
import CustomButton from '../../../components/OnboardingComponents/CustomButton';
import CustomEntryCard from '../../../components/OnboardingComponents/CustomEntryCard';
import { ICONS } from '../../../components/OnboardingComponents/icon';
import useCategories from '../../../hooks/useCategories';
import PreferredCitySelect from '../../../components/Common/PreferredCitySelect';
import { getUserAddressCityOptions } from '../../../pages/Profile/profileUser';
import {
  MAX_ONGOING_ROLES as MAX_ROLES,
  emptyOngoingRole as emptyRole,
  ongoingRoleSchema as roleSchema,
  toLocalOngoingRole as toLocalRole,
} from '../../../helpers/ongoingRoleForm';
import { setOngoingRoles } from '../../../store/slices/onboardingSlice';
import {
  ongoingRoleDraftHasContent,
  collectOngoingRoles,
  submitOngoingRoles,
  updateOngoingRole,
  deleteOngoingRole,
} from './onboardingOngoingRoleHelpers';

const MAX_VIA_ADD_BUTTON = MAX_ROLES - 1;

const Step1OngoingRoles = ({ onNext, editId, fromReview }) => {
  const dispatch = useDispatch();
  const items = useSelector((state) => state.onboarding?.ongoingRoles || []);
  const roles = useMemo(() => (items || []).map(toLocalRole), [items]);
  const currentUser = useSelector((state) => state.auth.currentUser);
  const cityOptions = useMemo(
    () => getUserAddressCityOptions(currentUser),
    [currentUser],
  );
  const [editingIndex, setEditingIndex] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasLoadedEdit = useRef(false);
  const { categories, getCategoryLabel } = useCategories();

  const resetValues = emptyRole();

  const formik = useFormik({
    initialValues: resetValues,
    validationSchema: roleSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values) => {
      if (editingIndex !== null) {
        const next = roles.map((role, i) => (i === editingIndex ? values : role));
        dispatch(setOngoingRoles(next));
        setEditingIndex(null);
      } else {
        if (roles.length >= MAX_VIA_ADD_BUTTON) return;
        dispatch(setOngoingRoles([...roles, values]));
      }
      formik.resetForm({ values: resetValues });
    },
  });

  const draftHasContent = ongoingRoleDraftHasContent(formik.values);
  const canAddAnother = roles.length < MAX_VIA_ADD_BUTTON;
  const showAddButton = editingIndex === null ? canAddAnother : true;
  const remaining =
    MAX_ROLES -
    roles.length -
    (draftHasContent && editingIndex === null ? 1 : 0);

  const categoryLabels = (values) =>
    values.map(getCategoryLabel).filter(Boolean).join(', ');

  const handleEdit = useCallback((index) => {
    setEditingIndex(index);
    formik.setValues(roles[index]);
  }, [formik, roles]);

  useEffect(() => {
    if (!editId || hasLoadedEdit.current) return;
    const index = roles.findIndex((role) => role.id === editId);
    if (index !== -1) {
      hasLoadedEdit.current = true;
      handleEdit(index);
    }
  }, [editId, roles, handleEdit]);

  const handleDelete = async (index) => {
    const role = roles[index];
    if (role?.id) {
      const deleted = await deleteOngoingRole(role.id);
      if (!deleted) return;
    }

    const next = roles.filter((_, i) => i !== index);
    dispatch(setOngoingRoles(next));

    if (editingIndex === index) {
      setEditingIndex(null);
      formik.resetForm({ values: resetValues });
    }
  };

  const handleNext = async () => {
    const final = await collectOngoingRoles({
      items: roles,
      formik,
      editingIndex,
    });
    if (final === null) return;

    const isEditing = editingIndex !== null;

    setIsSubmitting(true);
    try {
      if (isEditing && final[editingIndex]?.id) {
        const updated = await updateOngoingRole({ role: final[editingIndex] });
        if (updated) {
          dispatch(setOngoingRoles(final));
          formik.resetForm({ values: resetValues });
          setEditingIndex(null);
          if (fromReview) onNext?.(final);
        }
        return;
      }

      await submitOngoingRoles({
        roles: final,
        onSuccess: (nextRoles) => {
          formik.resetForm({ values: resetValues });
          setEditingIndex(null);
          dispatch(setOngoingRoles(nextRoles));
          if (!isEditing || fromReview) onNext?.(nextRoles);
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="amok-onboarding-step">
      <div className="amok-onboarding-step__header">
        <CustomStepBadge tone="info">Step 1 of 4</CustomStepBadge>
      </div>
      {roles.length > 0 && (
        <div className="amok-onboarding-step__list">
          {roles.map((role, i) => (
            <CustomEntryCard
              key={role.id || i}
              title={role.roleTitle}
              meta={[
                role.projectName,
                role.startDate,
                role.isOngoing ? 'Ongoing' : role.endDate,
                categoryLabels(role.categories),
              ].filter(Boolean)}
              onEdit={() => handleEdit(i)}
              onDelete={() => handleDelete(i)}
              editIconSrc={ICONS.edit}
              deleteIconSrc={ICONS.delete}
            />
          ))}
        </div>
      )}

      <h2 className="amok-onboarding-step__title f20 fw-500">Ongoing Roles</h2>
      <p className="amok-onboarding-step__subtitle f14">
        Add roles that are part of your long-term work &mdash; this step is optional
      </p>

      <div className="amok-onboarding-step__form">
        <RoleSelect
          label="Role Title"
          placeholder="e.g. Music Producer"
          value={formik.values.roleType}
          onChange={(role) => {
            formik.setFieldValue('roleType', role);
            formik.setFieldValue('roleTitle', role?.name || '');
            if (formik.errors.roleType) formik.setFieldError('roleType', undefined);
          }}
          error={formik.errors.roleType}
        />

        <PreferredCitySelect
          cityOptions={cityOptions}
          value={formik.values.preferredCity}
          onChange={(cityId) => {
            formik.setFieldValue('preferredCity', cityId);
            if (formik.errors.preferredCity) {
              formik.setFieldError('preferredCity', undefined);
            }
          }}
          error={formik.errors.preferredCity}
        />

        <CustomInput
          label="Project Name"
          placeholder="e.g. XYZ Record Label"
          value={formik.values.projectName}
          onChange={(e) => {
            formik.setFieldValue('projectName', e.target.value);
            if (formik.errors.projectName) formik.setFieldError('projectName', undefined);
          }}
          error={formik.errors.projectName}
        />

        <CategoriesMultiselect
          label="Categories"
          helperText="Select all that apply"
          options={categories}
          selected={formik.values.categories}
          onChange={(next) => {
            formik.setFieldValue('categories', next);
            if (formik.errors.categories) formik.setFieldError('categories', undefined);
          }}
          checkIconSrc={ICONS.check}
          error={formik.errors.categories}
        />

        <CustomDatePicker
          label="Date"
          startDate={formik.values.startDate}
          endDate={formik.values.endDate}
          onStartDateChange={(value) => {
            formik.setFieldValue('startDate', value);
            if (formik.errors.startDate) formik.setFieldError('startDate', undefined);
          }}
          onEndDateChange={(value) => {
            formik.setFieldValue('endDate', value);
            if (formik.errors.endDate) formik.setFieldError('endDate', undefined);
          }}
          isOngoing={formik.values.isOngoing}
          onOngoingChange={(checked) => {
            formik.setFieldValue('isOngoing', checked);
            if (checked) formik.setFieldValue('endDate', '');
          }}
          ongoingLabel="Still in this role"
          calendarIconSrc={ICONS.calendar}
          checkIconSrc={ICONS.check}
          error={[formik.errors.startDate, formik.errors.endDate]
            .filter(Boolean)
            .join(' ')}
        />
      </div>

      <div className="amok-onboarding-step__footer">
        <div className="amok-onboarding-step__footer-left">
          {showAddButton && (
            <CustomButton
              variant="secondary"
              size="md"
              iconSrc={ICONS.plus}
              disabled={!draftHasContent || (editingIndex === null && !canAddAnother) || isSubmitting}
              onClick={() => formik.submitForm()}
            >
              {editingIndex !== null ? 'Save role' : 'Add another role'}
            </CustomButton>
          )}
          <span className="amok-onboarding-step__hint">
            {remaining > 0
              ? `You can add up to ${MAX_ROLES} roles`
              : 'Maximum roles added'}
          </span>
        </div>

        <CustomButton
          variant="primary"
          size="md"
          trailingIconSrc={editingIndex !== null ? null : ICONS.arrowRight}
          onClick={handleNext}
          disabled={isSubmitting}
          loading={isSubmitting}
        >
          {isSubmitting
            ? 'Saving...'
            : editingIndex !== null
              ? 'Save'
              : 'Next'}
        </CustomButton>
      </div>
    </div>
  );
};

export default Step1OngoingRoles;
